import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSaga } from 'use-reducer-saga';
import { APP_URL, USERSROLE } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { currentUser } from 'redux/session/selectors';
import { setUserSession } from 'redux/session/actions';
import reducer, { initialState } from './localStore/reducer';
import sagas from './localStore/sagas';
import { acceptEULAStart, getEULAStart } from './localStore/actions';
import { UnAuthenticatedPageLayout } from 'layouts/unAuthenticatedPage.layout';
import { useComponentMount } from 'utilities/hooks.util';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  InlineNotification,
  Loading
} from 'carbon-components-react';
import { Information16 } from '@carbon/icons-react';

export const EULA: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const user = useSelector(currentUser);
  const [noticeText, setNoticeText] = useState<string>('');
  const [isError, setIsError] = useState<boolean>(false);
  const [localStore, localDispatch] = useSaga(reducer, initialState, sagas);
  const { process, error, eulaContent } = localStore;

  const onCancel = () => {
    history.push(APP_URL.LOGOUT);
  };

  useEffect(() => {
    if (process === 'success') {
      dispatch(setUserSession({ is_eula_accepted: true }));
    }
  }, [process, dispatch]);

  useEffect(() => {
    if (error) {
      setIsError(true);
      setNoticeText(error);
    }
  }, [error]);

  useEffect(() => {
    if (
      user.role_type_actual !== USERSROLE.TRIALUSER ||
      user?.is_eula_accepted === true
    ) {
      history.push(APP_URL.ROOT_URL);
    }
  }, [user, history]);

  useComponentMount(() => {
    localDispatch(getEULAStart());
  });

  return (
    <div id="eula" className="eula">
      <UnAuthenticatedPageLayout enableHeaderGlobalBar={false}>
        {isError && (
          <InlineNotification
            kind="error"
            title={noticeText}
            lowContrast={true}
            hideCloseButton={true}
          />
        )}
        {(process === 'loading' || process === 'accepting') && (
          <div className="eula-loading">
            <Loading withOverlay={false} />
          </div>
        )}
        {eulaContent && process !== 'accepting' && process !== 'loading' && (
          <ComposedModal
            open={true}
            preventCloseOnClickOutside={true}
            onClose={onCancel}
          >
            <ModalHeader>
              <div className="eula-modal-title">
                Website Terms and Conditions of Use
              </div>
            </ModalHeader>
            <ModalBody hasScrollingContent={true}>
              <div
                className="eula-modal-content"
                dangerouslySetInnerHTML={{
                  __html: eulaContent
                }}
              />
            </ModalBody>
            <ModalFooter>
              <div className="eula-modal-info">
                <Information16 />
                We&apos;ve updated our terms. Please agree to continue.
              </div>
              <Button kind="tertiary" onClick={onCancel}>
                Decline
              </Button>
              <Button
                kind="secondary"
                onClick={() => localDispatch(acceptEULAStart())}
              >
                I agree
              </Button>
            </ModalFooter>
          </ComposedModal>
        )}
      </UnAuthenticatedPageLayout>
    </div>
  );
};

EULA.displayName = 'EULA';

export default React.memo(EULA);
